import { useWorkspace } from "@/stores/workspace";
import { Library, Mic, Sparkles, Volume2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useSession } from "@/stores/session";
import { ADAPTERS } from "@/lib/adapters";

import { ELEVEN_VOICES } from "@/lib/voices";

export function Settings() {
  const {
    user,
    voiceEnabled,
    avatarEnabled,
    preferredVoiceId,
    toggleVoice,
    toggleAvatar,
    setVoice,
  } = useSession();

  return (
    <div className="container space-y-6 py-10">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight md:text-4xl">
          Settings
        </h1>
        <p className="text-muted-foreground">
          Tune how your tutor looks, sounds, and behaves.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_1.4fr]">
        <Card className="border-border/60 bg-card/60">
          <CardContent className="space-y-2 p-6">
            <Badge variant="default">Account</Badge>
            <h3 className="font-display text-lg font-semibold">{user?.name}</h3>
            <p className="text-sm text-muted-foreground">{user?.email}</p>
            <Separator className="my-4" />
            <div className="text-sm">
              Plan: <span className="font-medium capitalize">{user?.plan}</span>
            </div>
            <Button variant="outline" className="mt-4 w-full">
              Manage subscription
            </Button>
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card className="border-border/60 bg-card/60">
            <CardContent className="space-y-4 p-6">
              <Badge variant="accent">
                <Sparkles className="size-3" /> Tutor
              </Badge>
              <h3 className="font-display text-lg font-semibold">Voice & avatar</h3>
              <ToggleRow
                label="Avatar enabled"
                desc="Photoreal teacher with lip-synced speech"
                enabled={avatarEnabled}
                onToggle={toggleAvatar}
              />
              <ToggleRow
                label="Voice playback"
                desc="Hear the tutor narrate explanations"
                enabled={voiceEnabled}
                onToggle={toggleVoice}
              />
              <Separator />
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Volume2 className="size-4" /> Voice
                </div>
                <div className="grid gap-2 md:grid-cols-3">
                  {Object.entries(ELEVEN_VOICES).map(([id, v]) => (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setVoice(id)}
                      className={
                        "flex flex-col items-start gap-1 rounded-lg border px-3 py-3 text-left text-sm transition-all " +
                        (preferredVoiceId === id
                          ? "border-primary bg-primary/10"
                          : "border-border/60 bg-background/40")
                      }
                    >
                      <span className="font-medium">{v.name}</span>
                      <span className="text-xs text-muted-foreground">{v.desc}</span>
                    </button>
                  ))}
                </div>
              </div>
              <Separator />
              <div className="space-y-1 text-sm">
                <div className="flex items-center gap-2 font-medium">
                  <Mic className="size-4" /> Microphone permissions
                </div>
                <p className="text-xs text-muted-foreground">
                  Granted in browser settings.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="border-border/60 bg-card/60">
            <CardContent className="space-y-3 p-6">
              <Badge variant="outline">
                <Library className="size-3" /> Resources
              </Badge>
              <h3 className="font-display text-lg font-semibold">Curated GMAT directory</h3>
              <p className="text-sm text-muted-foreground">
                YouTube channels, forums, blogs, social accounts, podcasts, and official GMAC links.
              </p>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => useWorkspace.getState().setView("resources")}
              >
                Browse all resources
              </Button>
            </CardContent>
          </Card>

          <Card className="border-border/60 bg-card/60">
            <CardContent className="space-y-3 p-6">
              <Badge variant="default">Models</Badge>
              <h3 className="font-display text-lg font-semibold">
                Adapters routed to your tutor
              </h3>
              <p className="text-sm text-muted-foreground">
                Think Big Techies uses 16 fine-tuned LoRAs over Qwen2.5-1.5B-Instruct. PASS means
                this adapter measurably beats the base model on its held-out test set.
              </p>
              <div className="grid gap-2 md:grid-cols-2">
                {Object.values(ADAPTERS).map((a) => (
                  <div
                    key={a.name}
                    className="flex items-center justify-between rounded-lg border border-border/60 bg-background/40 px-3 py-2 text-xs"
                  >
                    <div className="min-w-0">
                      <div className="truncate font-mono text-foreground">
                        {a.name}
                      </div>
                      <div className="truncate text-muted-foreground">
                        {a.label}
                      </div>
                    </div>
                    <Badge variant={a.verified ? "success" : "outline"}>
                      {a.verified ? "PASS" : "trained"}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function ToggleRow({
  label,
  desc,
  enabled,
  onToggle,
}: {
  label: string;
  desc: string;
  enabled: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div>
        <div className="text-sm font-medium">{label}</div>
        <div className="text-xs text-muted-foreground">{desc}</div>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={enabled}
        onClick={onToggle}
        className={
          "relative inline-flex h-6 w-11 items-center rounded-full transition-colors " +
          (enabled ? "bg-primary" : "bg-secondary")
        }
      >
        <span
          className={
            "inline-block size-5 transform rounded-full bg-background shadow transition-transform " +
            (enabled ? "translate-x-5" : "translate-x-1")
          }
        />
      </button>
    </div>
  );
}
